import { Component, Input, Output, EventEmitter, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Attachment } from '@shared/entities/attachment';
import { ThemeService } from '../services/theme.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { FsSignedUrlService } from '@shared/services/api/fs-signed-url.service';
import { GenerateSignedUrlResponse } from '@shared/entities/generateSignedUrlResponse';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';

@Component({
  selector: 'app-storage-input-history',
  templateUrl: './storage-input-history.component.html',
  styleUrl: './storage-input-history.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StorageInputHistoryComponent),
      multi: true
    }
  ]
})
export class StorageInputHistoryComponent implements OnDestroy, ControlValueAccessor {
  @Input() titleKey = 'FILE_INPUT_HISTORY.DEFUALT_TITLE';
  @Input() uploadLabelKey = 'FILE_INPUT_HISTORY.DEFAULT_UPLOAD';
  @Input() noFilesKey = 'FILE_INPUT_HISTORY.NO_FILES_UPLOADED';
  @Input() accept = '.pdf,.doc,.docx';
  @Input() infoPopover?: string;
  @Input() attachments: Attachment[] | string[] = [];
  @Input() filterByAccept = false;
  @Input() hideUploadLabel = false;
  @Input() downloadProperty = 'id';
  @Input() autoUpload = true;
  @Input() isUploading = false;
  @Input() uploadProgress = 0;
  @Input() uploadError?: string | null;
  @Input() removingIds = new Set<string>();
  @Input() removeError?: string | null;

  @Input() onUpload?: (file: File) => void;
  @Input() onRemove?: (attachmentId: string) => void;

  @Output() fileUploaded = new EventEmitter<string>();
  @Output() fileDeleted = new EventEmitter<string>();
  @Output() pathsChanged = new EventEmitter<string[]>();
  @Output() uploadStarted = new EventEmitter<void>();
  @Output() uploadCompleted = new EventEmitter<string>();
  @Output() uploadFailed = new EventEmitter<string>();
  @Output() uploadProgressChange = new EventEmitter<number>();

  selectedFile?: File;
  internalUploadProgress = 0;
  uploadStatus?: string;
  signedUrlResponse?: GenerateSignedUrlResponse | null;
  uploadedFilePath?: string;

  fileControl = new FormControl<File | null>(null);
  downloadingIds = new Set<string>();

  readonly inputId = 'storage-input-history-' + Math.random().toString(36).slice(2, 9);

  private onChange: (v: any) => void = () => {
    /* noop */
  };
  private onTouched: () => void = () => {
    /* noop */
  };
  private destroy$ = new Subject<void>();
  disabled = false;

  constructor(
    private _themeService: ThemeService,
    private _fsAttachmentService: FsAttachmentService,
    private fsSignedUrlService: FsSignedUrlService,
    private http: HttpClient,
    public translate: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(fileOrFiles: File | File[] | null) {
    if (!fileOrFiles) return;

    const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;

    if (!file) return;

    this.selectedFile = file;
    this.internalUploadProgress = 0;
    this.uploadStatus = undefined;
    this.uploadError = undefined;
    this.signedUrlResponse = null;
    this.uploadedFilePath = undefined;

    this.onTouched();

    if (this.onUpload) {
      this.onUpload(this.selectedFile);
      return;
    }

    if (this.autoUpload) {
      this.startUpload();
    }
  }
  startUpload() {
    if (!this.selectedFile) {
      this.uploadError = this.translate.instant('FILE_INPUT_HISTORY.NO_FILE_SELECTED');
      return;
    }

    this.generateSignedUrl();
  }

  private generateSignedUrl() {
    if (!this.selectedFile) return;

    this.uploadError = undefined;
    this.uploadStatus = this.translate.instant('FILE_INPUT_HISTORY.GENERATING_URL');
    this.isUploading = true;

    const payload: any = {
      fileName: this.selectedFile.name
    };

    if (this.selectedFile.type) {
      payload.contentType = this.selectedFile.type;
    }

    this.fsSignedUrlService
      .generateSignedUrl(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.signedUrlResponse = res;
          this.uploadToSignedUrl();
        },
        error: (err) => {
          this.uploadError = typeof err === 'string' ? err : JSON.stringify(err);
          this.uploadStatus = this.translate.instant('FILE_INPUT_HISTORY.URL_GENERATION_ERROR');
          this.isUploading = false;
          this.uploadFailed.emit(this.uploadError as string);
        }
      });
  }

  private uploadToSignedUrl() {
    if (!this.signedUrlResponse || !this.signedUrlResponse.url || !this.selectedFile) {
      this.uploadError = this.translate.instant('FILE_INPUT_HISTORY.URL_NOT_AVAILABLE');
      this.isUploading = false;
      return;
    }

    this.uploadError = undefined;
    this.internalUploadProgress = 0;
    this.uploadStatus = this.translate.instant('FILE_INPUT_HISTORY.UPLOADING');
    this.uploadStarted.emit();

    const headers = new HttpHeaders({});

    this.http
      .put(this.signedUrlResponse.url, this.selectedFile, {
        headers,
        reportProgress: true,
        observe: 'events'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.internalUploadProgress = Math.round((100 * event.loaded) / event.total);
            this.uploadProgressChange.emit(this.internalUploadProgress);
          } else if (event.type === HttpEventType.Response) {
            this.internalUploadProgress = 100;
            this.uploadStatus = this.translate.instant('FILE_INPUT_HISTORY.UPLOAD_COMPLETED');

            if (this.signedUrlResponse?.url) {
              this.uploadedFilePath = this.extractPathFromSignedUrl(this.signedUrlResponse.url);

              const newAttachment = new Attachment();
              newAttachment.id = this.uploadedFilePath;
              newAttachment.filePath = this.uploadedFilePath;
              newAttachment.fileName = this.selectedFile?.name || '';
              newAttachment.contentType = this.selectedFile?.type;

              if (Array.isArray(this.attachments)) {
                (this.attachments as Attachment[]).push(newAttachment);
              } else {
                this.attachments = [newAttachment];
              }

              this.uploadCompleted.emit(this.uploadedFilePath);
              this.fileUploaded.emit(this.uploadedFilePath);
              this.emitPathsChanged();

              setTimeout(() => {
                this.selectedFile = undefined;
                this.internalUploadProgress = 0;
                this.uploadStatus = undefined;
                this.isUploading = false;
              }, 1500);
            }
          }
        },
        error: (err) => {
          this.uploadStatus = this.translate.instant('FILE_INPUT_HISTORY.UPLOAD_FAILED');
          this.isUploading = false;
          try {
            this.uploadError = typeof err === 'string' ? err : JSON.stringify(err);
          } catch {
            this.uploadError = this.translate.instant('FILE_INPUT_HISTORY.UPLOAD_FAILED_LOGS');
          }
          this.uploadFailed.emit(this.uploadError as string);
        }
      });
  }

  private extractPathFromSignedUrl(signedUrl: string): string {
    try {
      const url = new URL(signedUrl);
      const pathname = url.pathname;

      const parts = pathname.split('/').filter((p) => p);
      if (parts.length > 1) {
        return parts.slice(1).join('/');
      }

      return this.selectedFile?.name || '';
    } catch {
      return this.selectedFile?.name || '';
    }
  }

  downloadDocument(attachmentId: string, fileName?: string) {
    if (!attachmentId || this.downloadingIds.has(attachmentId)) {
      return;
    }

    this.downloadingIds.add(attachmentId);

    this._fsAttachmentService
      .downloadFile(attachmentId, fileName)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.downloadingIds.delete(attachmentId);
          return of(null);
        })
      )
      .subscribe(() => {
        this.downloadingIds.delete(attachmentId);
      });
  }

  deleteDocument(attachmentId: string) {
    if (this.onRemove) {
      this.onRemove(attachmentId);
      return;
    }

    const index = this.attachments.findIndex((att) => {
      const attId = this.getAttachmentId(att);
      return attId === attachmentId;
    });

    if (index > -1) {
      this.attachments.splice(index, 1);
    }

    if (this.attachments.length === 0) {
      this.onChange(null);
      this.fileControl.setValue(null);
    }

    this.fileDeleted.emit(attachmentId);
    this.emitPathsChanged();
  }

  private emitPathsChanged(): void {
    const paths = this.normalizeAttachments()
      .map((att) => att.filePath || att.id)
      .filter((path): path is string => !!path);
    this.pathsChanged.emit(paths);
  }

  isAttachmentArray(): boolean {
    if (this.attachments.length === 0) return false;
    return typeof this.attachments[0] !== 'string';
  }

  normalizeAttachments(): Attachment[] {
    if (this.attachments.length === 0) {
      return [];
    }

    if (typeof this.attachments[0] === 'string') {
      return (this.attachments as string[]).map((path) => {
        const fileName = path.split('/').pop() || path;
        const attachment = new Attachment();
        attachment.id = path;
        attachment.filePath = path;
        attachment.fileName = fileName;
        return attachment;
      });
    }

    return this.attachments as Attachment[];
  }

  isCurrentlyRemoving(attachmentId: string): boolean {
    return this.removingIds?.has(attachmentId) || false;
  }

  hasRemovingItems(): boolean {
    return this.removingIds ? this.removingIds.size > 0 : false;
  }

  isCurrentlyDownloading(attachmentId: string): boolean {
    return this.downloadingIds.has(attachmentId);
  }

  getAttachmentId(attachment: any): string | undefined {
    if (!attachment) return undefined;
    if (typeof attachment === 'string') return attachment;
    const prop = this.downloadProperty || 'id';
    return attachment[prop] ?? attachment.id;
  }

  get isUploadComplete(): boolean {
    const progress = this.onUpload ? this.uploadProgress : this.internalUploadProgress;
    return progress === 100 && !!this.uploadedFilePath;
  }

  get currentUploadProgress(): number {
    return this.onUpload ? this.uploadProgress : this.internalUploadProgress;
  }

  writeValue(obj: any): void {
    if (obj instanceof File) {
      this.fileControl.setValue(obj);
    } else {
      this.fileControl.setValue(null);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.fileControl.disable();
    } else {
      this.fileControl.enable();
    }
  }

  get isLight(): boolean {
    return this._themeService.isLight();
  }

  get filteredAttachments(): Attachment[] {
    const normalized = this.normalizeAttachments();

    if (!this.filterByAccept) {
      return normalized;
    }

    return normalized.filter((attachment) => {
      if (!attachment.contentType) {
        return false;
      }
      return this.matchesAcceptedType(attachment.contentType);
    });
  }

  private matchesAcceptedType(contentType: string): boolean {
    const acceptTypes = this.accept.split(',').map((t) => t.trim());

    for (const acceptType of acceptTypes) {
      if (acceptType.startsWith('.')) {
        const extension = acceptType.substring(1);
        if (contentType.includes(extension)) {
          return true;
        }
      } else if (acceptType.endsWith('/*')) {
        const typePrefix = acceptType.substring(0, acceptType.length - 2);
        if (contentType.startsWith(typePrefix + '/')) {
          return true;
        }
      } else if (contentType === acceptType) {
        return true;
      }
    }

    return false;
  }
}
